import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "./ui/sheet";
import { Menu } from "lucide-react";

const navLinks = [
  { path: "/", label: "Startseite" },
  { path: "/leistungen", label: "Leistungen" },
  { path: "/ueber-mich", label: "Über mich" },
  { path: "/blog", label: "Blog" },
  { path: "/kontakt", label: "Kontakt" }
];

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />

      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-white border-b transition-shadow duration-300 ${
          scrolled ? "shadow-md border-transparent" : "border-gray-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex flex-col">
              <span
                className="text-lg font-bold tracking-wide"
                style={{ color: "#1B3A5C", fontFamily: "'Inter', sans-serif" }}
              >
                RECHTSANWALT SINDROS
              </span>
              <span
                className="text-xs tracking-wider"
                style={{ color: "#2E75B6" }}
              >
                Kanzlei für Internetrecht
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium relative pb-1 ${
                    location.pathname === link.path
                      ? "text-[#1B3A5C]"
                      : "text-[#666666] hover:text-[#1B3A5C]"
                  }`}
                  style={{ transition: "color 0.2s ease" }}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#2E75B6] rounded-full" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <button className="p-2" aria-label="Menü öffnen">
                  <Menu className="h-6 w-6" style={{ color: "#1B3A5C" }} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] bg-white">
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <nav className="flex flex-col gap-6 mt-12">
                  {navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setMobileOpen(false)}
                      className={`text-base font-medium ${
                        location.pathname === link.path
                          ? "text-[#1B3A5C]"
                          : "text-[#666666] hover:text-[#1B3A5C]"
                      }`}
                      style={{ transition: "color 0.2s ease" }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-20">{children}</main>

      {/* Footer */}
      <footer style={{ backgroundColor: "#1B3A5C" }} className="text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Column 1 */}
            <div>
              <h3
                className="text-lg font-bold mb-2"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Rechtsanwalt Sindros
              </h3>
              <p className="text-sm text-white/70 leading-relaxed">
                Kanzlei für Internetrecht &amp; Reputationsmanagement
              </p>
              <p className="text-sm text-white/70 leading-relaxed mt-4">
                In Kooperation mit THE PUMA GROUP IKE
                <br />
                Thessaloniki, Griechenland
              </p>
            </div>

            {/* Column 2 */}
            <div>
              <h3 className="text-lg font-bold mb-4">Kontakt</h3>
              <p className="text-sm text-white/70 leading-relaxed">
                E-Mail: info@fairebewertungen.de
              </p>
              <p className="text-sm text-white/70 leading-relaxed mt-1">
                Telefon: +49 178 4660549
              </p>
            </div>

            {/* Column 3 */}
            <div>
              <h3 className="text-lg font-bold mb-4">Rechtliches</h3>
              <div className="flex flex-col gap-2">
                <Link
                  to="/impressum"
                  className="text-sm text-white/70 hover:text-white"
                  style={{ transition: "color 0.2s ease" }}
                >
                  Impressum
                </Link>
                <Link
                  to="/datenschutz"
                  className="text-sm text-white/70 hover:text-white"
                  style={{ transition: "color 0.2s ease" }}
                >
                  Datenschutzerklärung
                </Link>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 mt-12 pt-8 text-center">
            <p className="text-xs text-white/50">
              &copy; {new Date().getFullYear()} Rechtsanwalt Sindros. Alle Rechte
              vorbehalten.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
